/**
 * Pure page-area resolution for the stage shell. Every gate that has caused
 * a shipped regression lives here, table-tested:
 * - live surfaces keep their key across response updates; historical compositions
 *   are keyed by their accepted content identity;
 *   process/text pages use turn indexes while the optimistic request awaits
 *   its server identity,
 * - the restore fallback applies only to identity-less turns (a live turn
 *   that already has a responseId must not get overwritten by an older,
 *   unrelated resynced surface),
 * - nothing pre-empts the page during a request except real new content
 *   (surface, process page) — the previous page holds. Response text NEVER
 *   claims the page WHILE THE TURN IS IN FLIGHT, on any channel: streamed
 *   prose flashing onto the page and then getting swapped for the eventual
 *   surface is the regression this guards against,
 * - once a run settles with no surface of its own, compatibility and
 *   non-screen paths may still contain terminal text. Over an existing
 *   surface the stage preserves that artifact; without one it may use the
 *   retained `TextPage` compatibility renderer. Eligible typed browser
 *   turns normally reach this resolver only after server-side surface
 *   recovery has either rendered an artifact or produced a host error.
 *   Voice-opened turns hold the page because their answer was spoken.
 * - the "nothing to show" page is one honest state: `loading`. An idle
 *   stage with nothing renderable is not a designed product state — the
 *   welcome turn (or restored content) must always arrive. If `loading`
 *   is still on screen after the run settles, that is a BUG to fix at its
 *   root (a status lie, a restore gap), never a state to design a calm
 *   screen for.
 *
 * React-free: returns a descriptor; StagePage maps it to JSX.
 */
import type { TurnEntry } from './turn-index.ts';
import type { ReducedSurface } from '../../../../vendor/agentplace-a2ui/surface-reduction.ts';
import {
  BROWSER_VOICE_SCREEN_TEXT_MAX_CHARS,
  type BrowserVoiceScreenSelection,
  type PresentationTarget,
} from '../../../../../shared/ws-protocol.ts';

export type StageView =
  | {
      kind: 'surface';
      key: string;
      surfaceId: string;
      /** Present only when viewing a retained composition rather than the live artifact. */
      contentId?: string;
      snapshot?: ReducedSurface;
    }
  | { kind: 'process'; key: string; turnIndex: number }
  | { kind: 'text'; key: string; turnIndex: number }
  | { kind: 'unavailable'; key: string }
  | { kind: 'loading'; key: 'loading' };

export interface StageViewInput {
  turns: TurnEntry[];
  /** Rail selection; null = live head. */
  selectedIndex: number | null;
  /** Head at explicit submission: older content cannot replace the held origin page. */
  followAfterTurnId?: string;
  /** Live surface records in insertion order (latest last). */
  surfaces: ReadonlyArray<{
    id: string;
    responseId?: string;
    lastTouchedResponseId?: string;
    /** A new create frame still carries the previous answer's nodes. */
    awaitingReplacement?: boolean;
    /** Whether the surface has rendered any component yet. A surface exists from
     *  its `createSurface` frame, which the render tool emits BEFORE the first
     *  section is complete — so an empty one must not take the page, or the
     *  screen swaps to a blank frame and fills in afterwards. */
    hasContent?: boolean;
  }>;
  /** Stable key of the active, registered process part; null otherwise. */
  processPartKey: string | null;
  userRequestPending: boolean;
  /** A voice-originated run is working — the process page must not be gated
   *  on typed sends alone; spoken requests deserve the same working page. */
  voiceRunActive?: boolean;
}

/** The page may show a completed progressive section before the run ends.
 *  Working chrome therefore follows the response lifecycle, never the page
 *  descriptor selected by `resolveStageView`. */
export function isStageRunActive(userRequestPending: boolean): boolean {
  return userRequestPending;
}

/** Capture the selected surface at dispatch; non-surface views have no continuation target. */
export function presentationTargetForStage(view: StageView): PresentationTarget | undefined {
  if (view.kind !== 'surface') {
    return undefined;
  }
  return {
    surfaceId: view.surfaceId,
    ...(view.contentId ? { contentId: view.contentId } : {}),
  };
}

/**
 * Normalizes the page the browser actually renders into the small wire
 * contract voice understands. The server resolves surface structure and
 * values; the browser supplies only its attachment-local selection.
 */
export function voiceScreenSelectionForStage(options: {
  view: StageView;
  turns: ReadonlyArray<TurnEntry>;
  processNarration: string;
  copy: {
    transcriptOpen: string;
    workInProgress: string;
    screenTextTruncated: string;
  };
}): BrowserVoiceScreenSelection {
  switch (options.view.kind) {
    case 'surface':
      return {
        kind: 'surface',
        surfaceId: options.view.surfaceId,
        ...(options.view.contentId ? { contentId: options.view.contentId } : {}),
      };
    case 'text': {
      const text = boundedVisibleText(
        options.turns[options.view.turnIndex]?.responseText ||
          options.turns[options.view.turnIndex]?.surfaceFallback ||
          '',
        options.copy.screenTextTruncated,
      );
      return text ? { kind: 'text', text } : null;
    }
    case 'process': {
      const narration = boundedVisibleText(
        options.processNarration,
        options.copy.screenTextTruncated,
      );
      return { kind: 'text', text: narration || options.copy.workInProgress };
    }
    case 'loading':
    case 'unavailable':
      return null;
  }
}

function boundedVisibleText(text: string, marker: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= BROWSER_VOICE_SCREEN_TEXT_MAX_CHARS) {
    return trimmed;
  }
  const separatedMarker = ` ${marker}`;
  return `${trimmed.slice(0, BROWSER_VOICE_SCREEN_TEXT_MAX_CHARS - separatedMarker.length)}${separatedMarker}`;
}

/** Only a page with real content is worth carrying forward across a turn —
 *  `loading` is a "nothing yet" state, never a held page. */
function isHoldable(view: StageView): boolean {
  return view.kind !== 'loading';
}

function latestRenderableSurface(
  surfaces: StageViewInput['surfaces'],
): StageViewInput['surfaces'][number] | undefined {
  return [...surfaces].reverse().find((surface) => surface.hasContent !== false);
}

/**
 * A turn selects its last completed Surface output. Creator/last-touch matching
 * remains for progressive and legacy turns without a retained content anchor.
 *
 * A surface with no content yet is NOT a match: it exists only because
 * `createSurface` precedes the first section, and taking the page at that
 * moment blanks whatever the visitor was reading. Skipping it keeps the hold
 * in place until there is something to swap TO, so the screen changes once.
 */
function surfaceForTurn(
  surfaces: StageViewInput['surfaces'],
  turn: TurnEntry,
): StageViewInput['surfaces'][number] | undefined {
  const surfaceId = turn.surfaceIds.at(-1);
  if (surfaceId) {
    return surfaces.find(
      (candidate) =>
        candidate.id === surfaceId &&
        !candidate.awaitingReplacement &&
        candidate.hasContent !== false,
    );
  }
  if (!turn.responseId) {
    return undefined;
  }
  return surfaces.find(
    (candidate) =>
      candidate.hasContent !== false &&
      !candidate.awaitingReplacement &&
      (candidate.responseId === turn.responseId ||
        candidate.lastTouchedResponseId === turn.responseId),
  );
}

export function findCarriedSurfaceId(
  turns: ReadonlyArray<TurnEntry>,
  surfaces: StageViewInput['surfaces'],
  index: number,
): string | null {
  for (let i = index; i >= 0; i--) {
    const turn = turns[i];
    if (!turn) {
      continue;
    }
    const surface = surfaceForTurn(surfaces, turn);
    if (surface) {
      return surface.id;
    }
  }
  return null;
}

/**
 * `prevLive` is the last PAGE this resolver produced AT THE LIVE HEAD
 * (browsing alone must not replace it). An explicit submission may instead
 * seed it with the selected historical page and `followAfterTurnId`.
 * It is returned unchanged when the live turn has produced nothing yet:
 * the page stays, one transition per turn.
 *
 * One rule governs the order below: **never disturb a page to signal that work
 * is happening.** A process page replaces whatever the visitor was reading, so
 * when a screen is already up it is held instead — swapping to `process` reads
 * as the whole page refreshing and discards the screen continuity exists to
 * preserve. The hold returns `prevLive` BY REFERENCE, because "same object"
 * is the signal consumers use to skip work.
 *
 * The same rule decides what compatibility text shows. Over an existing screen
 * it resolves to that surface; only when no artifact exists can the retained
 * compatibility renderer own the page (`{kind:'text'}`). New typed browser
 * behavior is governed by the surface-delivery contract and recovery path, not
 * by a reserved prose destination here.
 */
export function resolveStageView(input: StageViewInput, prevLive: StageView | null): StageView {
  const liveHeadIndex = input.turns.length - 1;
  const resolvedIndex = input.selectedIndex !== null ? input.selectedIndex : liveHeadIndex;
  const turn = resolvedIndex >= 0 ? input.turns[resolvedIndex] : undefined;
  const atLiveHead = input.selectedIndex === null || input.selectedIndex === liveHeadIndex;

  if (!atLiveHead) {
    return resolveHistoryView(input, resolvedIndex, turn);
  }

  // Identity match requires an identity on BOTH sides: `undefined ===
  // undefined` would "match" any resynced (identity-less) surface — showing
  // the wrong restored surface and letting a stale one pre-empt a pending
  // request through the match branch.
  const matched = turn ? surfaceForTurn(input.surfaces, turn) : undefined;
  if (
    input.followAfterTurnId !== undefined &&
    prevLive?.kind === 'surface' &&
    prevLive.contentId &&
    (turn?.id === input.followAfterTurnId || !matched)
  ) {
    return prevLive;
  }
  let displaySurfaceId = matched?.id ?? null;

  // Restore fallback: only when the turn's identity is UNKNOWN (restored
  // history after a reload, or an echo edge) and nothing is in flight.
  if (!displaySurfaceId && !input.userRequestPending && !turn?.responseId) {
    const latest = input.surfaces[input.surfaces.length - 1];
    displaySurfaceId = latest ? latest.id : null;
  }

  if (displaySurfaceId) {
    return {
      kind: 'surface',
      key: `surface:${displaySurfaceId}`,
      surfaceId: displaySurfaceId,
    };
  }

  if (input.userRequestPending && prevLive?.kind === 'surface') {
    const previousStillExists = input.surfaces.some((surface) => surface.id === prevLive.surfaceId);
    if (previousStillExists) {
      return prevLive;
    }
    const restored = latestRenderableSurface(input.surfaces);
    if (restored) {
      return {
        kind: 'surface',
        key: `surface:${restored.id}`,
        surfaceId: restored.id,
      };
    }
  }

  if ((input.userRequestPending || input.voiceRunActive) && input.processPartKey) {
    return { kind: 'process', key: `process:${input.processPartKey}`, turnIndex: resolvedIndex };
  }

  if (!input.userRequestPending && turn?.responseText && turn.channel !== 'voice') {
    const carriedSurfaceId = findCarriedSurfaceId(input.turns, input.surfaces, resolvedIndex);
    if (carriedSurfaceId) {
      return {
        kind: 'surface',
        key: `surface:${carriedSurfaceId}`,
        surfaceId: carriedSurfaceId,
      };
    }
    return { kind: 'text', key: `text:${resolvedIndex}`, turnIndex: resolvedIndex };
  }

  if (prevLive && isHoldable(prevLive)) {
    return prevLive;
  }
  return { kind: 'loading', key: 'loading' };
}

/**
 * History selects accepted compositions, never applies them back to live state.
 * A turn without its own visual output carries the preceding composition.
 */
function resolveHistoryView(
  input: StageViewInput,
  resolvedIndex: number,
  turn: TurnEntry | undefined,
): StageView {
  for (let index = resolvedIndex; index >= 0; index--) {
    const previous = input.turns[index];
    if (!previous?.surfaceContentId) {
      continue;
    }
    if (previous.surfaceSnapshot) {
      return {
        kind: 'surface',
        key: `content:${previous.surfaceContentId}`,
        surfaceId: previous.surfaceSnapshot.surfaceId,
        contentId: previous.surfaceContentId,
        snapshot: previous.surfaceSnapshot,
      };
    }
    // Legacy records may retain readable output but no replayable composition.
    // Do not silently substitute a newer version of their artifact.
    if (previous.surfaceFallback || previous.responseText) {
      return { kind: 'text', key: `text:${index}`, turnIndex: index };
    }
    return { kind: 'unavailable', key: `unavailable:${previous.surfaceContentId}` };
  }
  const matched = turn ? surfaceForTurn(input.surfaces, turn) : undefined;
  if (matched) {
    return {
      kind: 'surface',
      key: `surface:${matched.id}`,
      surfaceId: matched.id,
    };
  }
  if (turn?.responseText) {
    const carriedSurfaceId = findCarriedSurfaceId(input.turns, input.surfaces, resolvedIndex);
    if (carriedSurfaceId) {
      return {
        kind: 'surface',
        key: `surface:${carriedSurfaceId}`,
        surfaceId: carriedSurfaceId,
      };
    }
    return { kind: 'text', key: `text:${resolvedIndex}`, turnIndex: resolvedIndex };
  }
  return { kind: 'loading', key: 'loading' };
}
